import React, { useEffect, useMemo, useRef, useState } from "react";
import "./contactus.scss";
import axios from "axios";
import COUNTRIES from "./countries.js";

const digitsOnly = (value) => value.replace(/\D/g, "");

const toE164 = (country, local) => {
  if (!country) return "";
  let localDigits = digitsOnly(local);
  const dialDigits = digitsOnly(country.dial);
  if (localDigits.startsWith("0")) localDigits = localDigits.slice(1);
  if (localDigits.startsWith(dialDigits)) localDigits = localDigits.slice(dialDigits.length);
  return `${country.dial}${localDigits}`;
};

const Contactus = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [prefixQuery, setPrefixQuery] = useState("");
  const prefixRef = useRef(null);
  const searchRef = useRef(null);
  const phoneRef = useRef(null);
  const phoneFieldRef = useRef(null);

  const filteredCountries = useMemo(() => {
    const q = prefixQuery.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.dial.includes(q) ||
        item.iso.toLowerCase().includes(q)
    );
  }, [prefixQuery]);

  useEffect(() => {
    if (!prefixOpen) return undefined;
    const onPointerDown = (event) => {
      const inPrefix = prefixRef.current && prefixRef.current.contains(event.target);
      const inLockedPhone =
        !country && phoneFieldRef.current && phoneFieldRef.current.contains(event.target);
      if (!inPrefix && !inLockedPhone) {
        setPrefixOpen(false);
        setPrefixQuery("");
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setPrefixOpen(false);
        setPrefixQuery("");
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    if (searchRef.current) searchRef.current.focus();
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [prefixOpen, country]);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    if (!name.trim()) {
      setNameError("Enter a valid name.");
      valid = false;
    }
    if (!validateEmail(email)) {
      setEmailError("Enter a valid email address.");
      valid = false;
    }
    if (!country || digitsOnly(toE164(country, phoneNumber)).length < 8) {
      setPhoneError("Enter a valid phone number.");
      valid = false;
    }
    if (!valid) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      await axios.post("https://api.mezada.net/contact", {
        email,
        phoneNumber: toE164(country, phoneNumber),
        name,
      });
      setSent(true);
    } catch (error) {
      console.error("Error:", error);
      setSubmitError("Could not send your message. Please email info@mezada.net.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setName("");
    setEmail("");
    setPhoneNumber("");
    setCountry(null);
    setEmailError("");
    setNameError("");
    setPhoneError("");
    setSubmitError("");
    setPrefixOpen(false);
    setPrefixQuery("");
    setSent(false);
  };

  const selectCountry = (item) => {
    setCountry(item);
    setPrefixOpen(false);
    setPrefixQuery("");
    setPhoneError("");
    requestAnimationFrame(() => {
      if (phoneRef.current) phoneRef.current.focus();
    });
  };

  return (
    <section id="contact" className="mz-contact mz-grid">
      <div className="mz-wrap mz-contact__inner">
        <div className="mz-contact__header">
          <div className="mz-chip">CONTACT</div>
          <div className="mz-contact__heading">
            <h2 className="mz-contact__title">Let's build something.</h2>
            <p className="mz-contact__lead">
              Tell us what you're building and where it needs to go. We reply within one business day.
            </p>
          </div>
          <a className="mz-contact__mail" href="mailto:info@mezada.net">
            info@mezada.net
          </a>
        </div>

        <div className="mz-form">
          {sent ? (
            <div className="mz-success">
              <div className="mz-success__icon">
                <img src="/assets/figma/success-check.svg" alt="" />
                <span>✓</span>
              </div>
              <h3>Message sent.</h3>
              <p>Thanks for reaching out. We reply within one business day.</p>
              <button type="button" onClick={reset}>
                Send another message
              </button>
            </div>
          ) : (
            <form className="mz-form__stack" onSubmit={handleSubmit} noValidate>
              <div className="mz-form__fields">
                <div className="mz-field-wrap">
                  <div className={`mz-field ${nameError ? "is-error" : ""}`}>
                    <label htmlFor="name">Name</label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Your full name"
                      value={name}
                      aria-invalid={!!nameError}
                      aria-describedby={nameError ? "name-error" : undefined}
                      onChange={(e) => {
                        setName(e.target.value);
                        setNameError("");
                      }}
                    />
                  </div>
                  {nameError && (
                    <p id="name-error" className="mz-field-error" role="alert">
                      {nameError}
                    </p>
                  )}
                </div>

                <div className="mz-field-wrap">
                  <div className={`mz-field ${emailError ? "is-error" : ""}`}>
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? "email-error" : undefined}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError("");
                      }}
                    />
                  </div>
                  {emailError && (
                    <p id="email-error" className="mz-field-error" role="alert">
                      {emailError}
                    </p>
                  )}
                </div>

                <div className="mz-field-wrap">
                  <div className="mz-phone-row">
                    <div
                      className={`mz-prefix${prefixOpen ? " is-open" : ""}${
                        phoneError && !country ? " is-error" : ""
                      }`}
                      ref={prefixRef}
                    >
                      <button
                        type="button"
                        className="mz-prefix__btn"
                        aria-haspopup="listbox"
                        aria-expanded={prefixOpen}
                        aria-label={country ? `Country code ${country.dial}` : "Select country code"}
                        onClick={() => setPrefixOpen((open) => !open)}
                      >
                        <span className="mz-prefix__flag" aria-hidden="true">
                          {country ? country.flag : "🌐"}
                        </span>
                        <span className={`mz-prefix__label${country ? "" : " is-placeholder"}`}>
                          {country ? country.dial : "Country"}
                        </span>
                        <span className="mz-prefix__caret" aria-hidden="true">
                          ▾
                        </span>
                      </button>
                      {prefixOpen && (
                        <div className="mz-prefix__menu" role="listbox">
                          <input
                            ref={searchRef}
                            className="mz-prefix__search"
                            type="text"
                            value={prefixQuery}
                            placeholder="Search country"
                            aria-label="Search country"
                            onChange={(e) => setPrefixQuery(e.target.value)}
                          />
                          <div className="mz-prefix__list">
                            {filteredCountries.length === 0 && (
                              <p className="mz-prefix__empty">No countries found.</p>
                            )}
                            {filteredCountries.map((item) => (
                              <button
                                key={item.iso}
                                type="button"
                                role="option"
                                aria-selected={country && country.iso === item.iso}
                                className={`mz-prefix__option${
                                  country && country.iso === item.iso ? " is-active" : ""
                                }`}
                                onClick={() => selectCountry(item)}
                              >
                                <span className="mz-prefix__option-flag">{item.flag}</span>
                                <span className="mz-prefix__option-name">{item.name}</span>
                                <span className="mz-prefix__option-dial">{item.dial}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      ref={phoneFieldRef}
                      className={`mz-field mz-field--phone ${phoneError ? "is-error" : ""}${
                        country ? "" : " is-disabled"
                      }`}
                      onMouseDown={(e) => {
                        if (country) return;
                        e.preventDefault();
                        setPrefixOpen(true);
                      }}
                    >
                      <label htmlFor="phone">Phone</label>
                      <input
                        id="phone"
                        ref={phoneRef}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel-national"
                        placeholder="50 123 4567"
                        value={phoneNumber}
                        disabled={!country}
                        aria-invalid={!!phoneError}
                        aria-describedby={phoneError ? "phone-error" : undefined}
                        onChange={(e) => {
                          setPhoneNumber(e.target.value);
                          setPhoneError("");
                        }}
                      />
                    </div>
                  </div>
                  {phoneError && (
                    <p id="phone-error" className="mz-field-error" role="alert">
                      {phoneError}
                    </p>
                  )}
                </div>
              </div>
              <button type="submit" className="mz-form__submit" disabled={submitting}>
                {submitting ? "Sending…" : "Send message"}
              </button>
              {submitError && (
                <p className="mz-field-error" role="alert">
                  {submitError}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contactus;
